import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";
import { Check } from "lucide-react";

interface PricingTier {
  id: string;
  name: string;
  description: string;
  price: {
    amount: number | null;
    currency: string;
    period: string;
    displayText?: string;
  };
  features: string[];
  cta: { text: string };
  highlighted?: boolean;
  badge?: string;
}

interface PricingProps {
  title: string;
  subtitle?: string;
  billingNote?: string;
  tiers: PricingTier[];
}

/**
 * Pricing
 *
 * Pricing table with:
 * - Highlighted "popular" tier with accent glow
 * - Feature checklist
 * - Responsive grid layout
 * - CTA buttons with appropriate styles
 */
export function Pricing({ title, subtitle, billingNote, tiers }: PricingProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="pricing" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Pricing</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-[#8A8F98] text-base lg:text-lg mb-4">
              {subtitle}
            </p>
          )}

          {billingNote && (
            <p className="text-[#8A8F98] text-sm">{billingNote}</p>
          )}
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {tiers.map((tier, index) => (
            <SpotlightCard
              key={tier.id}
              className={cn(
                "relative",
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8",
                tier.highlighted && [
                  "border-[#5E6AD2]/50",
                  "shadow-[0_0_0_1px_rgba(94,106,210,0.3),0_8px_40px_rgba(94,106,210,0.15)]",
                ]
              )}
              style={{
                transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
              }}
            >
              {/* Popular badge */}
              {tier.badge && (
                <div
                  className={cn(
                    "absolute -top-3 left-1/2 -translate-x-1/2",
                    "px-3 py-1 rounded-full",
                    "bg-[#5E6AD2] text-white text-xs font-medium",
                    "shadow-accent-glow"
                  )}
                >
                  {tier.badge}
                </div>
              )}

              <SpotlightCardContent className="p-6 lg:p-8">
                {/* Tier header */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-[#EDEDEF] mb-2">
                    {tier.name}
                  </h3>
                  <p className="text-[#8A8F98] text-sm">{tier.description}</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  {tier.price.amount !== null ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl lg:text-5xl font-semibold headline-gradient">
                        ${tier.price.amount}
                      </span>
                      <span className="text-[#8A8F98] text-sm">
                        /{tier.price.period}
                      </span>
                    </div>
                  ) : (
                    <div className="text-4xl lg:text-5xl font-semibold headline-gradient">
                      {tier.price.displayText || "Custom"}
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <Link to="/signup" className="block w-full mb-6">
                  <Button
                    className={cn(
                      "w-full",
                      tier.highlighted
                        ? [
                            "bg-[#5E6AD2] hover:bg-[#6872D9] text-white",
                            "shadow-accent-glow",
                            "btn-shine",
                          ]
                        : [
                            "bg-white/[0.05] hover:bg-white/[0.08]",
                            "text-[#EDEDEF]",
                            "border border-white/[0.1]",
                          ]
                    )}
                  >
                    {tier.cta.text}
                  </Button>
                </Link>

                {/* Features list */}
                <ul className="space-y-3">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Check
                        className={cn(
                          "w-4 h-4 mt-0.5 flex-shrink-0",
                          tier.highlighted ? "text-[#5E6AD2]" : "text-[#8A8F98]"
                        )}
                      />
                      <span className="text-[#8A8F98]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </SpotlightCardContent>
            </SpotlightCard>
          ))}
        </div>
      </div>
    </section>
  );
}
